import React, {FunctionComponent, useState} from 'react';
import {Option, useForm} from 'b302-frontend-library';

import Country from '../../../../networking/models/Country';
import HanDropdown from '../../../utils/HanDropdown';
import Continent from '../../../../networking/models/Continent';
import HanButton from '../../../utils/HanButton';
import EditCard from '../../layout/EditCard';
import CountryCard from '../../partials/country/CountryCard';
import {toast} from 'react-toastify';
import {useCountryIndexQueryConfig} from '../../../query/Country/Query';
import {useQuery} from '@tanstack/react-query';
import {
    useCountryDeleteMutation,
    useCountrySaveMutation
} from '../../../query/Country/Mutation';

interface NewCountry {
    number: number;
}

export interface CountriesForm {
    continent: number;
}

interface Props {
    continents: Continent[];
}

const CountriesContent: FunctionComponent<Props> = (props) => {

    const [form, onFormChange] = useForm<CountriesForm>(f => ({
        continent: f.select(-1, props.continents.map(c => c.id))
    }));

    const [countryConfig] = useCountryIndexQueryConfig(form.values.continent);
    const countryQuery = useQuery(countryConfig);

    const countrySaveMutation = useCountrySaveMutation();
    const countryDeleteMutation = useCountryDeleteMutation();

    const [newCountries, setNewCountries] = useState<NewCountry[]>([]);

    const addCountry = () => {
        setNewCountries([
            ...newCountries, { number: Math.random() }
        ]);
    };

    const onNewCountryNameChange = async (name: string, number: number) => {
        await saveCountry({ name });

        deleteNewCountry(number);
    };

    const saveCountry = async (country: Country) => {
        const request = countrySaveMutation.mutateAsync({ country, continentId: form.values.continent });

        await toast.promise(request, {
            pending: `Saving country ${country.name}`,
            success: `Saved country ${country.name}!`,
            error: `Error saving country ${country.name}!`
        });
    };

    const deleteNewCountry = (number: number) => {
        const newState = [...newCountries];

        const index = newState.findIndex(c => c.number === number);
        newState.splice(index, 1);

        setNewCountries(newState);
    };

    const deleteCountry = async (country: Country) => {
        await toast.promise(countryDeleteMutation.mutateAsync({ country, continentId: form.values.continent }), {
            pending: `Deleting country ${country.name}`,
            success: `Deleted country ${country.name}`,
            error: `Error deleting country ${country.name}`,
        });
    };

    const countries = countryQuery.data || [];

    return (
        <>
            <h1>Countries { countries.length ? `(${countries.length})` : '' }</h1>

            <div className={'admin-countries-page__content'}>
                <div className={'admin-countries-page__region'}>
                    <span className={'admin-countries-page__section-title'}>Region</span>

                    <div>
                        <HanDropdown form={form} onChange={onFormChange} id={'continent'} placeholder={'Select Region'}>
                            { (item, i, onClick) => {
                                const continent = props.continents.find(c => c.id == item)!;

                                return  <Option onClick={() => onClick(i, item)} key={continent.id}>{ continent.name }</Option>
                            } }
                        </HanDropdown>

                        <HanButton onClick={addCountry} disabled={form.values.continent === -1}>Add country</HanButton>
                    </div>
                </div>

                <div className={'admin-countries-page__countries'}>
                    <span className={'admin-countries-page__section-title'}>Country</span>

                    { (!countries.length) && <span>Select a region.</span> }

                    { newCountries.map(newCountry => (
                        <EditCard
                            value={''}
                            open={true}
                            onChange={(name) => onNewCountryNameChange(name, newCountry.number)}
                            onDelete={() => deleteNewCountry(newCountry.number)}
                            key={newCountry.number} />
                    )) }

                    { countries.map(country => (
                        <CountryCard
                            country={country}
                            onChange={saveCountry}
                            onDelete={deleteCountry}
                            key={country.id} />
                    )) }
                </div>
            </div>
        </>
    );
};

export default CountriesContent;
